import { pipe } from 'fp-ts/function';
import { concatAll } from 'fp-ts/Monoid';
import * as N from 'fp-ts/number';
import * as O from 'fp-ts/Ord';
import * as S from 'fp-ts/string';
import { STATUS_OPTIONS } from 'src/modules/basedata/constants/statusOptions';
import { AuthUser } from 'src/modules/common/types/AuthUser';
import { ordNullable, ordPrecedence } from 'src/modules/common/utils/ord';
import { ProjectListItem } from 'src/modules/project-list/types/ProjectListItem';
import { ProjectListSort } from 'src/modules/project-list/types/ProjectListSort';

export function getProjectOrd(sort: ProjectListSort, authUser: AuthUser): O.Ord<ProjectListItem> {
  const ord = concatAll(O.getMonoid<ProjectListItem>())([
    SORT_MAP(authUser, sort.direction)[sort.field],
    SORT_BY_ID,
  ]);

  return sort.direction === 'asc' ? ord : O.reverse(ord);
}

const SORT_BY_ID = pipe(
  S.Ord,
  O.contramap((item: ProjectListItem) => item.project.id),
);

const SORT_MAP = (authUser: AuthUser, direction: 'asc' | 'desc'): Readonly<
Record<ProjectListSort['field'],
O.Ord<ProjectListItem>>
> => ({
  title: pipe(
    S.Ord,
    O.contramap((item) => item.project.data.title),
  ),
  status: pipe(
    N.Ord,
    O.contramap((item) => STATUS_OPTIONS.findIndex((option) => option === item.project.data.status)),
  ),
  rating: pipe(
    N.Ord,
    O.contramap((item) => item.rating.future?.toNumber() ?? -1),
  ),
  updated: pipe(
    N.Ord,
    O.contramap((item) => item.project.updatedAt.getTime()),
  ),
  members: pipe(
    S.Ord,
    ordPrecedence(authUser.email),
    ordNullable,
    O.contramap((item) => item.project.owner?.email ?? null),
  ),
  roi: pipe(
    ordWithNullsBottom(direction),
    O.contramap((item) => item.metrics.roiExclCO2?.toNumber()),
  ),
  roi_2: pipe(
    ordWithNullsBottom(direction),
    O.contramap((item) => item.metrics.roiExclCO2_2?.toNumber()),
  ),
  roi_5: pipe(
    ordWithNullsBottom(direction),
    O.contramap((item) => item.metrics.roiExclCO2_5?.toNumber()),
  ),
  bep: pipe(
    ordWithNullsBottom(direction),
    O.contramap((item) => item.metrics.bepExclCO2?.toNumber()),
  ),
});

const ordWithNullsBottom = (direction: 'asc' | 'desc'): O.Ord<number | undefined> => O.fromCompare((a, b) => {
  if ((a == null) && (b == null)) {
    return 0;
  }
  if (a == null) {
    return direction === 'asc' ? 1 : -1;
  }
  if (b == null) {
    return direction === 'asc' ? -1 : 1;
  }

  return N.Ord.compare(a, b);
});
